import { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { useAppDispatch, useAppSelector } from '@verantos-dev/app-state-base';
import { useCohortBuilderData } from '@verantos-dev/data-access-cohorts';
import {
  CohortStatusLegacy,
  FilterTypeLegacy,
  SubjectVariableCriteriaSelectionTypeLegacy,
  SubjectVariableTypeLegacy,
} from '@verantos-dev/types-cohorts';

import { useCanAddFilters } from './useCanAddFilters';
import { useCanSaveCohortItem } from './useCanSaveCohortItem';
import { useCohortBuilderItemIsInUse } from './useCohortBuilderItemIsInUse';
import { useCohortEditable } from './useCohortEditable';
import { useConceptFilters } from './useConceptFilters';
import { useCountFilter } from './useCountFilter';
import { useNumericFilters } from './useNumericFilters';
import { useSubjectVariablesScroll } from './useSubjectVariablesScroll';
import {
  clearModal,
  setActiveCohortBuilderItemFilterInputFieldValue,
  setActiveCohortBuilderItemName,
  setActiveCohortBuilderItemSubjectVariableType,
  setSubjectVariable,
} from '../cohortBuilderSlice';
import { NAME_VALID_CHARACTERS_WITHOUT_DOUBLE_QUOTE } from '../constants/constants';
import {
  selectActiveCohortBuilderItemId,
  selectActiveCohortBuilderItemInputFieldConfigMap,
  selectActiveCohortBuilderItemName,
  selectActiveCohortBuilderItemSubjectVariableType,
} from '../selectors/cohortBuilderEditModalSelectors';
import { selectModal } from '../selectors/cohortBuilderWorkflowSelectors';
import {
  selectCohortDsl,
  selectDerivedEvents,
  selectSimpleEvents,
  selectSubjectVariableById,
  selectSubjectVariables,
} from '../selectors/cohortDefinitionSelectors';
import { validateName } from '../utils/checkNameUniqueness';
import { getFullCohortDslForSubjectsVariables } from '../utils/dsl-generators/getFullCohortDslForSubjectsVariables';

export const useSubjectVariablesEdit = () => {
  const dispatch = useAppDispatch();
  const { isEditable } = useCohortEditable();
  const { data: cohortProfileData } = useCohortBuilderData();
  const isProcessing = cohortProfileData?.data.latestStatus === CohortStatusLegacy.PROCESSING;

  /** Cohort Builder Workflow - Edit Modal State */
  const cohortDsl = useAppSelector(selectCohortDsl);
  const subjectVariableId = useAppSelector(selectActiveCohortBuilderItemId) ?? uuidv4(); // if no id, then we are creating a new subject variable
  const subjectVariableIsInUse = useCohortBuilderItemIsInUse(subjectVariableId);

  // Summary State
  const name = useAppSelector(selectActiveCohortBuilderItemName) ?? '';
  const type = useAppSelector(selectActiveCohortBuilderItemSubjectVariableType);

  const modal = useAppSelector(selectModal);

  // Filter State
  const { conceptFilters, onAddConceptFilterClick, onDeleteConceptFilterClick } =
    useConceptFilters();
  const { numericFilters, onAddNumericFilterClick, onDeleteNumericFilterClick } =
    useNumericFilters();
  const { countFilter } = useCountFilter();

  const inputFieldConfigMap =
    useAppSelector(selectActiveCohortBuilderItemInputFieldConfigMap) ?? {};

  /** Cohort Builder Definition State */
  const currentSubjectVariable = useAppSelector((state) =>
    selectSubjectVariableById(state, subjectVariableId)
  );

  /** Cohort Builder Edit Modal Internal State Side Effects */
  const { isSaveAllowed } = useCanSaveCohortItem<SubjectVariableTypeLegacy>(name, type, [
    ...conceptFilters,
    ...numericFilters,
    ...(countFilter ? [countFilter] : []),
  ]);

  const { headerRef, dialogContentRef, conceptFilterRef, numericFilterRef } =
    useSubjectVariablesScroll(conceptFilters, numericFilters);

  const { enableAddFilterButtons, addFilterButtonTitleText, typeInputFieldTitleText } =
    useCanAddFilters<SubjectVariableTypeLegacy>(name, type, subjectVariableIsInUse);

  /** user to check for duplicate names */
  const subjectVariables = useAppSelector(selectSubjectVariables);
  const derivedEvents = useAppSelector(selectDerivedEvents);
  const simpleEvents = useAppSelector(selectSimpleEvents);

  const [nameInError, setNameInError] = useState<boolean>(false);
  const [nameErrorMsg, setNameErrorMsg] = useState<string>('');

  const [selectedIECriteria, setSelectedIECriteria] =
    useState<SubjectVariableCriteriaSelectionTypeLegacy>(
      currentSubjectVariable?.criteriaSelection ??
        SubjectVariableCriteriaSelectionTypeLegacy.INCLUSION
    );

  const onNameChange = (_id: string, value: string) => {
    dispatch(
      setActiveCohortBuilderItemName({
        id: subjectVariableId,
        name: value,
      })
    );

    setNameErrorMsg('');
    setNameInError(false);
  };

  const onNameBlur = (id: string, value: string, inError: boolean, trimValue: string) => {
    const { errorMsg, hasError } = validateName(
      subjectVariableId,
      name,
      subjectVariables ?? [],
      derivedEvents ?? [],
      simpleEvents ?? []
    );
    setNameErrorMsg(errorMsg);
    setNameInError(hasError);
  };

  // check on modal/card open (on mount) using useEffect:
  useEffect(() => {
    const { errorMsg, hasError } = validateName(
      subjectVariableId,
      name,
      subjectVariables ?? [],
      derivedEvents ?? [],
      simpleEvents ?? []
    );
    setNameErrorMsg(errorMsg);
    setNameInError(hasError);
  }, [name, derivedEvents, simpleEvents, subjectVariables, subjectVariableId]);

  const onTypeChange = (id: string, value: string) => {
    value &&
      dispatch(
        setActiveCohortBuilderItemSubjectVariableType({
          id: subjectVariableId,
          type: value as SubjectVariableTypeLegacy,
        })
      );
  };

  const onFilterInputValueChange = (
    filterId: string,
    inputFieldId: string,
    value: string,
    filterType: FilterTypeLegacy
  ) => {
    dispatch(
      setActiveCohortBuilderItemFilterInputFieldValue({
        filterType,
        filterId,
        inputFieldId,
        value,
      })
    );
  };

  /** Cohort Builder Edit Modal Action Handlers */
  const onSaveSubjectVariable = (emitResearchAssistantUpdate: (nextCohortDsl: string) => void) => {
    const subjectVariable = {
      id: subjectVariableId,
      name: name.trim(),
      type: type,
      conceptFilters,
      numericFilters,
      countFilter,
      criteriaSelection: selectedIECriteria,
    };
    dispatch(setSubjectVariable(subjectVariable));

    const nextCohortDsl = getFullCohortDslForSubjectsVariables(
      subjectVariables || [],
      subjectVariable,
      cohortDsl
    );
    emitResearchAssistantUpdate(nextCohortDsl);
  };

  const onCancelSubjectVariable = () => {
    dispatch(clearModal());
  };

  const onSelectInclusionCriteria = () =>
    setSelectedIECriteria(SubjectVariableCriteriaSelectionTypeLegacy.INCLUSION);

  const onSelectExclusionCriteria = () =>
    setSelectedIECriteria(SubjectVariableCriteriaSelectionTypeLegacy.EXCLUSION);

  const onSelectNotAppliedCriteria = () =>
    setSelectedIECriteria(SubjectVariableCriteriaSelectionTypeLegacy.NOT_APPLIED);

  return {
    /** Cohort Builder Edit Modal State */
    name,
    type,
    modal,
    enableAddFilterButtons,
    conceptFilters,
    numericFilters,
    countFilter,
    isSaveAllowed,
    inputFieldConfigMap,
    subjectVariableIsInUse,
    addFilterButtonTitleText,
    typeInputFieldTitleText,

    /** Cohort Builder Edit Modal Internal State Side Effects */
    onNameChange,
    onNameBlur,
    nameInError,
    nameErrorMsg,
    nameValidCharacters: NAME_VALID_CHARACTERS_WITHOUT_DOUBLE_QUOTE,
    onTypeChange,
    onFilterInputValueChange,
    onAddConceptFilterClick,
    onDeleteConceptFilterClick,
    onDeleteNumericFilterClick,
    onAddNumericFilterClick,

    /** Cohort Builder Edit Modal Action Handlers */
    onSaveSubjectVariable,
    onCancelSubjectVariable,

    /** scroll into view newly created actions **/
    headerRef,
    dialogContentRef,
    conceptFilterRef,
    numericFilterRef,

    /** I/E criteria */
    isInclusionCriteriaSelected:
      selectedIECriteria === SubjectVariableCriteriaSelectionTypeLegacy.INCLUSION,
    isExclusionCriteriaSelected:
      selectedIECriteria === SubjectVariableCriteriaSelectionTypeLegacy.EXCLUSION,
    onSelectInclusionCriteria,
    onSelectExclusionCriteria,
    onSelectNotAppliedCriteria,

    isEditable,
    isProcessing,
    cohortDsl,
  };
};
